import { useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { Button, Modal } from "@monolith-forensics/monolith-ui";

import ButtonMenu from "@/components/Forms/components/ButtonMenu";
import { isConnectionFailure } from "@/api/connection.types";
import { AssetValidationsAPI } from "@/Features/AssetValidations/asset-validations.api";
import {
  assetValidationQueryKeys,
  invalidateLabAssetForValidation,
} from "@/Features/AssetValidations/asset-validations.query";
import type { AssetValidation } from "@/Features/AssetValidations/asset-validations.types";
import { toast } from "@/utils/toastService";
import { getValidationLabel } from "../Forms/validationForm.utils";

interface DeleteValidationModalProps {
  show: boolean;
  onClose: () => void;
  // Fires only after the API confirms the delete; the flyout closes itself.
  onDelete?: (deleted: AssetValidation) => void;
  defaultInfo: AssetValidation;
}

// Mirrors DeleteLabAssetModal. A failed delete toasts and leaves the modal
// open for a retry. A 404 is silent: the row is already gone, so it gets the
// same cache cleanup as a success. Report attachments survive the delete as
// plain asset attachments (the api nulls validation_uuid).
export const DeleteValidationModal: React.FC<DeleteValidationModalProps> = ({
  show,
  onClose,
  onDelete,
  defaultInfo,
}) => {
  const queryClient = useQueryClient();
  const [submitting, setSubmitting] = useState(false);

  const handleSubmit = async () => {
    setSubmitting(true);
    let result: unknown;
    try {
      result = await AssetValidationsAPI.delete(defaultInfo.uuid, {
        skipErrorToast: true,
      });
    } finally {
      setSubmitting(false);
    }
    if (isConnectionFailure(result) && result.error?.response?.status !== 404) {
      toast.error(
        result.error?.response?.data?.message ||
          "An error occurred while deleting this validation."
      );
      return;
    }

    queryClient.removeQueries({
      queryKey: assetValidationQueryKeys.item(defaultInfo.uuid),
    });
    queryClient.invalidateQueries({
      queryKey: assetValidationQueryKeys.infiniteLists,
    });
    invalidateLabAssetForValidation(queryClient, defaultInfo.asset_uuid);
    onDelete?.(defaultInfo);
    onClose();
  };

  return (
    <Modal open={show} onClose={onClose}>
      <Modal.Title>Delete Validation</Modal.Title>
      <div style={{ fontWeight: 600, margin: "20px 0px" }}>
        Are you sure you want to delete this validation: "
        {getValidationLabel(defaultInfo)}"?
      </div>
      <ButtonMenu>
        <Button
          size="xs"
          variant="subtle"
          onClick={onClose}
          disabled={submitting}
        >
          Cancel
        </Button>
        <Button
          size="xs"
          color="error"
          variant="contained"
          onClick={handleSubmit}
          disabled={submitting}
          loading={submitting}
        >
          Delete Validation
        </Button>
      </ButtonMenu>
    </Modal>
  );
};
